import React, { useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Trash2, LogOut, Printer, FileDown, Ban, RotateCcw, CheckCircle2, Send, PoundSterling } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAgent } from '../../contexts/AgentContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useToast } from '../../hooks/use-toast';
import WelcomeLetter from '../../components/paperwork/WelcomeLetter';
import PolicyCertificate from '../../components/paperwork/PolicyCertificate';
import Invoice from '../../components/paperwork/Invoice';
import TermsConditions, { TermsPage1, TermsPage2, TermsPage3, TermsPage4 } from '../../components/paperwork/TermsConditions';
import CancellationLetter from '../../components/paperwork/CancellationLetter';
import CancellationInvoice from '../../components/paperwork/CancellationInvoice';
import RenewalLetter from '../../components/paperwork/RenewalLetter';
import EmailTemplates from '../../components/agent/EmailTemplates';
import CancellationDialog from '../../components/agent/CancellationDialog';
import PartialPaymentDialog from '../../components/agent/PartialPaymentDialog';
import SendPaperworkDialog from '../../components/agent/SendPaperworkDialog';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { authedFetch } from '../../utils/staffAuth';

const baseTabs = [
  { id: 'welcome', label: 'Welcome Letter' },
  { id: 'certificate', label: 'Policy Certificate' },
  { id: 'invoice', label: 'Invoice' },
  { id: 'renewal', label: 'Renewal Letter' },
  { id: 'terms', label: 'Terms & Conditions' },
];

const cancelTabs = [
  { id: 'cancellation-letter', label: 'Cancellation Letter' },
  { id: 'cancellation-invoice', label: 'Cancellation Invoice' },
];

const captureNodeToPdfPage = async (node, pdf, isFirst) => {
  const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
  const imgData = canvas.toDataURL('image/png');
  const pdfW = pdf.internal.pageSize.getWidth();
  const pdfH = pdf.internal.pageSize.getHeight();
  const imgH = (canvas.height * pdfW) / canvas.width;
  if (!isFirst) pdf.addPage();
  pdf.addImage(imgData, 'PNG', 0, 0, pdfW, Math.min(imgH, pdfH));
};

const PolicyDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { logout } = useAgent();
  const { policies, deletePolicy, markDocumentsGenerated, cancelPolicy, reactivatePolicy, markCancellationPaid, updatePolicy, recordPartialPayment } = usePlatform();
  const [tab, setTab] = useState('welcome');
  const [dl, setDl] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const { toast } = useToast();
  const printRef = useRef(null);

  const policy = policies.find((p) => p.id === id);

  if (!policy) {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center">
        <div className="text-center">
          <p className="font-bold text-[#1F0068]">Policy not found</p>
          <Link to="/agent" className="text-[#FF5C39] underline">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const isCancelled = policy.status === 'Cancelled' || !!policy.cancellation;
  const tabs = isCancelled ? [...baseTabs, ...cancelTabs] : baseTabs;

  const downloadPack = async () => {
    try {
      setDl('pack');
      markDocumentsGenerated(policy.id);
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p' });
      const ids = [
        'pw-welcome', 'pw-certificate', 'pw-invoice',
        'pw-terms-1', 'pw-terms-2', 'pw-terms-3', 'pw-terms-4',
      ];
      if (isCancelled) ids.push('pw-cancellation-letter', 'pw-cancellation-invoice');
      let first = true;
      for (const nid of ids) {
        const node = document.getElementById(nid);
        if (node) { await captureNodeToPdfPage(node, pdf, first); first = false; }
      }
      pdf.save(`${policy.planNumber}_Policy_Pack.pdf`);
      toast({ title: 'PDF downloaded', description: `${policy.planNumber}_Policy_Pack.pdf saved.` });
    } catch (e) {
      toast({ title: 'Could not build PDF', description: String(e) });
    } finally {
      setDl(null);
    }
  };

  const downloadInvoice = async () => {
    try {
      setDl('inv');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p' });
      const node = document.getElementById('pw-invoice');
      if (node) await captureNodeToPdfPage(node, pdf, true);
      pdf.save(`${policy.planNumber}_Invoice.pdf`);
      toast({ title: 'Invoice downloaded', description: `${policy.planNumber}_Invoice.pdf saved.` });
    } catch (e) {
      toast({ title: 'Could not build PDF', description: String(e) });
    } finally {
      setDl(null);
    }
  };

  const downloadRenewal = async () => {
    try {
      setDl('ren');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p' });
      const node = document.getElementById('pw-renewal');
      if (node) await captureNodeToPdfPage(node, pdf, true);
      pdf.save(`${policy.planNumber}_Renewal_Letter.pdf`);
      toast({ title: 'Renewal letter downloaded' });
    } catch (e) {
      toast({ title: 'Could not build PDF', description: String(e) });
    } finally {
      setDl(null);
    }
  };

  const downloadCancellationInvoiceOnly = async () => {
    try {
      setDl('caninv');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p' });
      const node = document.getElementById('pw-cancellation-invoice');
      if (node) await captureNodeToPdfPage(node, pdf, true);
      pdf.save(`${policy.planNumber}_Cancellation_Invoice.pdf`);
      toast({ title: 'Cancellation invoice downloaded' });
    } catch (e) {
      toast({ title: 'Could not build PDF', description: String(e) });
    } finally {
      setDl(null);
    }
  };

  const downloadCancellationPack = async () => {    try {
      setDl('can');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p' });
      const ids = ['pw-cancellation-letter', 'pw-cancellation-invoice'];
      let first = true;
      for (const nid of ids) {
        const node = document.getElementById(nid);
        if (node) { await captureNodeToPdfPage(node, pdf, first); first = false; }
      }
      pdf.save(`${policy.planNumber}_Cancellation_Pack.pdf`);
      toast({ title: 'Cancellation PDF downloaded' });
    } catch (e) {
      toast({ title: 'Could not build PDF', description: String(e) });
    } finally {
      setDl(null);
    }
  };

  const printCurrent = () => {
    const node = printRef.current;
    if (!node) return;
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) return toast({ title: 'Please allow pop-ups to print' });
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map((el) => el.outerHTML).join('\n');
    w.document.write(`<html><head><title>${policy.planNumber}</title>${styles}<style>@page{size:A4;margin:0}body{margin:0;background:#fff}</style></head><body>${node.outerHTML}<script>window.onload=function(){setTimeout(function(){window.print();},300);};<\/script></body></html>`);
    w.document.close();
  };

  const remove = () => {
    if (!window.confirm('Delete this policy? This cannot be undone.')) return;
    deletePolicy(policy.id);
    toast({ title: 'Policy deleted' });
    nav('/agent');
  };

  const confirmCancel = (payload) => {
    cancelPolicy(policy.id, payload);
    setCancelOpen(false);
    setTab('cancellation-letter');
    toast({ title: 'Policy cancelled', description: `Cancellation paperwork ready for £${payload.amount.toFixed(2)}.` });
  };

  const markPaid = async () => {
    if (!policy.cancellation) return;
    if (!window.confirm(`Mark the £${Number(policy.cancellation.amount || 0).toFixed(2)} cancellation settlement as paid? This will confirm the cancellation, email the customer a confirmation, and count the amount as revenue.`)) return;
    markCancellationPaid(policy.id);
    toast({ title: 'Cancellation confirmed', description: 'Sending confirmation email to the customer…' });
    // Fire the cancellation confirmation email now that the charge is paid.
    try {
      const { buildEmail } = await import('../../components/agent/emailTemplateBuilder');
      const tpl = buildEmail('cancellation', { ...policy, cancellation: { ...policy.cancellation, confirmed: true, paidAt: new Date().toISOString() } });
      const authFetchResult = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: policy.email, subject: tpl.subject, html: tpl.html, text: tpl.text, tag: 'cancellation-confirmation' }),
      }, toast);
      if (!authFetchResult) return; // 401 handled
      const { res, json: j } = authFetchResult;
      if (res.ok && j?.status === 'sent') toast({ title: 'Confirmation email delivered', description: `Cancellation confirmation sent to ${policy.email}.` });
      else toast({ title: 'Payment recorded — email queued', description: 'Please retry the confirmation email from the Emails panel if it did not arrive.' });
    } catch (e) {
      toast({ title: 'Payment recorded — email not sent', description: 'Please send the cancellation confirmation manually from the Emails panel.' });
    }
  };

  const reactivate = () => {
    if (!window.confirm('Reactivate this policy? The cancellation record will be cleared.')) return;
    reactivatePolicy(policy.id);
    setTab('welcome');
    toast({ title: 'Policy reactivated' });
  };

  const sendCancellationReminder = async () => {
    if (!policy.cancellation || policy.cancellation.confirmed) return;
    if (!policy.email) return toast({ title: 'No customer email on file' });
    if (!window.confirm(`Send the friendly cancellation reminder email for £${Number(policy.cancellation.amount || 0).toFixed(2)} to ${policy.email}?`)) return;
    try {
      const { buildEmail } = await import('../../components/agent/emailTemplateBuilder');
      const tpl = buildEmail('cancellation-reminder', policy);
      const authFetchResult = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: policy.email, subject: tpl.subject, html: tpl.html, text: tpl.text, tag: 'cancellation-reminder' }),
      }, toast);
      if (!authFetchResult) return; // 401 handled
      const { res, json: j } = authFetchResult;
      if (res.ok && j?.status === 'sent') {
        toast({ title: 'Reminder email sent', description: `Delivered to ${policy.email}.` });
        // Stamp the reminder timestamp locally + persist to Mongo so the auto-scheduler skips this policy.
        const at = new Date().toISOString();
        const events = [...(policy.paymentEvents || []), { at, status: 'Cancellation Reminder Sent', note: `Reminder emailed to ${policy.email} (manual)` }];
        updatePolicy(policy.id, {
          cancellation: { ...policy.cancellation, reminderSentAt: at, reminderCount: Number(policy.cancellation.reminderCount || 0) + 1 },
          paymentEvents: events,
        });
      } else {
        toast({ title: 'Send failed', description: j?.detail || 'Please retry from the Emails panel.' });
      }
    } catch (e) {
      toast({ title: 'Send failed', description: String(e?.message || e) });
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA]">
      <header className="bg-[#0F0532] text-white print:hidden">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/agent" className="flex items-center gap-2">
            <svg viewBox="0 0 40 40" className="w-8 h-8">
              <circle cx="20" cy="20" r="20" fill="#FF5C39" />
              <path d="M12 22 L18 28 L28 14" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="leading-tight"><div className="font-bold text-sm">Domestic Home Services</div><div className="text-[10px] tracking-[0.22em] text-white/60 uppercase">Agent Portal</div></div>
          </Link>
          <Button variant="ghost" onClick={() => { logout(); nav('/agent/login'); }} className="gap-1.5 text-white/85 hover:bg-white/10 rounded-full"><LogOut className="w-4 h-4" /> Sign out</Button>
        </div>
      </header>

      <main className="py-8 print:py-0">
        <div className="max-w-6xl mx-auto px-5 print:px-0">
          <div className="print:hidden">
            <Link to="/agent" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F0068]/70 hover:text-[#1F0068] mb-4">
              <ArrowLeft className="w-4 h-4" /> Back to dashboard
            </Link>

            <div className="bg-white rounded-2xl border border-[#1F0068]/5 p-6 mb-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs uppercase tracking-widest font-bold text-[#FF5C39]">Plan {policy.planNumber}</div>
                    {isCancelled && <span data-testid="policy-cancelled-badge" className="text-[10px] uppercase tracking-widest font-bold text-white bg-[#FF5C39] rounded-full px-2 py-0.5">Cancelled</span>}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#1F0068] tracking-tight mt-1">{policy.title} {policy.firstName} {policy.lastName}</h1>
                  <div className="text-[#1F0068]/70 mt-1 text-sm">{policy.address1}{policy.address2 ? ', ' + policy.address2 : ''}, {policy.city}, {policy.postcode}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="download-pack-btn" onClick={downloadPack} disabled={dl === 'pack'} className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white gap-1.5">
                    <Download className="w-4 h-4" /> {dl === 'pack' ? 'Building PDF…' : 'Download full pack (PDF)'}
                  </Button>
                  <Button data-testid="email-paperwork-btn" onClick={() => setSendOpen(true)} className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5">
                    <Send className="w-4 h-4" /> Email paperwork
                  </Button>
                  <Button onClick={downloadInvoice} disabled={dl === 'inv'} variant="outline" className="rounded-full border-[#FF5C39] text-[#FF5C39] hover:bg-[#FF5C39] hover:text-white gap-1.5">
                    <FileDown className="w-4 h-4" /> {dl === 'inv' ? 'Building…' : 'Invoice only (PDF)'}
                  </Button>
                  <Button data-testid="download-renewal-btn" onClick={downloadRenewal} disabled={dl === 'ren'} variant="outline" className="rounded-full border-[#1F0068] text-[#1F0068] hover:bg-[#1F0068] hover:text-white gap-1.5">
                    <FileDown className="w-4 h-4" /> {dl === 'ren' ? 'Building…' : 'Renewal letter (PDF)'}
                  </Button>
                  {isCancelled && (
                    <>
                      <Button data-testid="download-cancellation-btn" onClick={downloadCancellationPack} disabled={dl === 'can'} variant="outline" className="rounded-full border-[#FF5C39] text-[#FF5C39] hover:bg-[#FF5C39] hover:text-white gap-1.5">
                        <FileDown className="w-4 h-4" /> {dl === 'can' ? 'Building…' : 'Cancellation pack (PDF)'}
                      </Button>
                      <Button data-testid="download-cancellation-invoice-btn" onClick={downloadCancellationInvoiceOnly} disabled={dl === 'caninv'} variant="outline" className="rounded-full border-[#FF5C39] text-[#FF5C39] hover:bg-[#FF5C39] hover:text-white gap-1.5">
                        <FileDown className="w-4 h-4" /> {dl === 'caninv' ? 'Building…' : 'Cancellation invoice only (PDF)'}
                      </Button>
                    </>
                  )}
                  <Button onClick={printCurrent} variant="outline" className="rounded-full border-[#1F0068] text-[#1F0068] hover:bg-[#1F0068] hover:text-white gap-1.5"><Printer className="w-4 h-4" /> Print</Button>
                  {isCancelled ? (
                    <Button data-testid="reactivate-btn" onClick={reactivate} variant="outline" className="rounded-full border-[#00b67a] text-[#00b67a] hover:bg-[#00b67a] hover:text-white gap-1.5"><RotateCcw className="w-4 h-4" /> Reactivate</Button>
                  ) : (
                    <>
                      {policy.paymentStatus !== 'Paid' && (
                        <Button data-testid="record-partial-payment-btn" onClick={() => setPartialOpen(true)} variant="outline" className="rounded-full border-[#1F0068] text-[#1F0068] hover:bg-[#1F0068] hover:text-white gap-1.5"><PoundSterling className="w-4 h-4" /> Record partial payment</Button>
                      )}
                      <Button data-testid="cancel-policy-btn" onClick={() => setCancelOpen(true)} variant="outline" className="rounded-full border-[#FF5C39] text-[#FF5C39] hover:bg-[#FF5C39] hover:text-white gap-1.5"><Ban className="w-4 h-4" /> Cancel policy</Button>
                    </>
                  )}
                  <Button onClick={remove} variant="outline" className="rounded-full border-[#1F0068]/20 text-[#1F0068]/70 hover:bg-[#1F0068]/10 gap-1.5"><Trash2 className="w-4 h-4" /> Delete</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <div className="bg-[#FFF6EB] rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Amount (Annual)</div><div className="text-xl font-extrabold text-[#1F0068]">£ {Number(policy.amount).toFixed(2)}</div></div>
                <div className="bg-[#FFF6EB] rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Start</div><div className="text-sm font-bold text-[#1F0068]">{new Date(policy.policyStart).toLocaleDateString('en-GB')}</div></div>
                <div className="bg-[#FFF6EB] rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">End</div><div className="text-sm font-bold text-[#1F0068]">{new Date(policy.policyEnd).toLocaleDateString('en-GB')}</div></div>
                <div className="bg-[#FFF6EB] rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Payment due</div><div className="text-sm font-bold text-[#1F0068]">{new Date(policy.paymentDue).toLocaleDateString('en-GB')}</div></div>
              </div>

              {(() => {
                const partials = Array.isArray(policy.partialPayments) ? policy.partialPayments : [];
                if (partials.length === 0 && policy.paymentStatus !== 'Partially Paid') return null;
                const total = Number(policy.amount || 0);
                const paidSum = partials.reduce((s, pp) => s + Number(pp.amount || 0), 0);
                const paid = policy.paymentStatus === 'Paid' ? total : Math.min(total, paidSum);
                const outstanding = Math.max(0, +(total - paid).toFixed(2));
                const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
                return (
                  <div data-testid="payment-progress" className="mt-4 bg-[#E8DDFF] rounded-xl p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/70 font-bold">Payment progress</div>
                        <div className="text-sm text-[#1F0068]/80 mt-0.5">£{paid.toFixed(2)} received of £{total.toFixed(2)} · <b className={outstanding <= 0 ? 'text-[#00b67a]' : 'text-[#FF5C39]'}>{outstanding <= 0 ? 'Fully paid' : `£${outstanding.toFixed(2)} outstanding`}</b></div>
                      </div>
                      <div className="text-xl font-extrabold text-[#1F0068]" data-testid="payment-progress-pct">{pct}%</div>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-white/70 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#1F0068] to-[#FF5C39] transition-all" style={{ width: pct + '%' }} />
                    </div>
                    {partials.length > 0 && (
                      <div className="mt-3 grid gap-1 text-[11px] text-[#1F0068]/85">
                        {partials.map((pp, i) => (
                          <div key={pp.at + '-' + i} className="flex items-center justify-between border-b border-[#1F0068]/10 last:border-0 py-1">
                            <div><b className="text-[#1F0068]">£{Number(pp.amount || 0).toFixed(2)}</b><span className="text-[#1F0068]/60"> · {new Date(pp.at).toLocaleDateString('en-GB')}</span>{pp.note ? <span className="text-[#1F0068]/60"> · {pp.note}</span> : null}</div>
                            <span className="text-[10px] uppercase font-bold text-[#00b67a]">Received</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {isCancelled && policy.cancellation && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-[#FF5C39]/10 rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Cancellation amount</div><div className="text-xl font-extrabold text-[#FF5C39]">£ {Number(policy.cancellation.amount || 0).toFixed(2)}</div></div>
                  <div className="bg-[#FF5C39]/10 rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Cancelled on</div><div className="text-sm font-bold text-[#1F0068]">{new Date(policy.cancellation.cancelledAt).toLocaleDateString('en-GB')}</div></div>
                  {policy.cancellation.paidAt ? (
                    <div className="bg-[#00b67a]/10 rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#00b67a] font-bold">Settlement paid</div><div className="text-sm font-bold text-[#1F0068]">{new Date(policy.cancellation.paidAt).toLocaleDateString('en-GB')}</div><div className="text-[10px] text-[#1F0068]/60 mt-0.5">Counted as revenue</div></div>
                  ) : (
                    <div className="bg-[#FFF6EB] rounded-xl p-4 flex items-center justify-between gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Settlement status</div>
                        <div className="text-sm font-bold text-[#FF5C39]">Awaiting payment</div>
                        {policy.cancellation.reminderSentAt && (
                          <div data-testid="cancellation-reminder-stamp" className="text-[10px] text-[#1F0068]/60 mt-0.5">Reminder sent {new Date(policy.cancellation.reminderSentAt).toLocaleDateString('en-GB')}{policy.cancellation.reminderCount > 1 ? ` (×${policy.cancellation.reminderCount})` : ''}</div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Button data-testid="mark-cancellation-paid-btn" size="sm" onClick={markPaid} className="rounded-full bg-[#00b67a] hover:bg-[#00a068] text-white gap-1 text-xs h-8"><CheckCircle2 className="w-3 h-3" /> Mark paid</Button>
                        <Button data-testid="send-cancellation-reminder-btn" size="sm" variant="outline" onClick={sendCancellationReminder} className="rounded-full border-[#FF5C39] text-[#FF5C39] hover:bg-[#FF5C39] hover:text-white gap-1 text-xs h-8"><Send className="w-3 h-3" /> Send reminder</Button>
                      </div>
                    </div>
                  )}
                  {policy.cancellation.reason && <div className="bg-[#FF5C39]/10 rounded-xl p-4 md:col-span-1"><div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Reason</div><div className="text-sm text-[#1F0068]">{policy.cancellation.reason}</div></div>}
                </div>
              )}

              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Appliances covered</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(policy.appliances || []).map((a) => (
                    <span key={a} className="px-3 py-1 bg-[#FFEDD6] text-[#1F0068] font-semibold text-xs rounded-full">{a}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Email templates */}
            <div className="mb-6">
              <EmailTemplates policy={policy} />
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-t-2xl border border-b-0 border-[#1F0068]/5 px-4 pt-3 flex items-center gap-1 overflow-x-auto">
              {tabs.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg whitespace-nowrap ${tab === t.id ? 'bg-[#1F0068] text-white' : 'text-[#1F0068]/70 hover:bg-[#FFF6EB]'}`}>
                  <FileText className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview area */}
          <div className="bg-[#E5E0D6] print:bg-white p-4 md:p-8 rounded-b-2xl print:p-0 print:rounded-none">
            <div ref={printRef} className="mx-auto" style={{ maxWidth: '820px' }}>
              {tab === 'welcome' && <WelcomeLetter policy={policy} />}
              {tab === 'certificate' && <PolicyCertificate policy={policy} />}
              {tab === 'invoice' && <Invoice policy={policy} />}
              {tab === 'renewal' && <RenewalLetter policy={policy} />}
              {tab === 'terms' && <TermsConditions policy={policy} />}
              {tab === 'cancellation-letter' && isCancelled && <CancellationLetter policy={policy} />}
              {tab === 'cancellation-invoice' && isCancelled && <CancellationInvoice policy={policy} />}
            </div>
          </div>

          {/* Hidden but rendered so html2canvas can capture */}
          <div style={{ position: 'absolute', left: -10000, top: 0, width: '820px' }} aria-hidden="true">
            <div id="pw-welcome"><WelcomeLetter policy={policy} /></div>
            <div id="pw-certificate"><PolicyCertificate policy={policy} /></div>
            <div id="pw-invoice"><Invoice policy={policy} /></div>
            <div id="pw-renewal"><RenewalLetter policy={policy} /></div>
            <div id="pw-terms-1"><TermsPage1 policy={policy} /></div>
            <div id="pw-terms-2"><TermsPage2 policy={policy} /></div>
            <div id="pw-terms-3"><TermsPage3 policy={policy} /></div>
            <div id="pw-terms-4"><TermsPage4 policy={policy} /></div>
            {isCancelled && (
              <>
                <div id="pw-cancellation-letter"><CancellationLetter policy={policy} /></div>
                <div id="pw-cancellation-invoice"><CancellationInvoice policy={policy} /></div>
              </>
            )}
          </div>
        </div>
      </main>

      <CancellationDialog
        open={cancelOpen}
        policy={policy}
        onClose={() => setCancelOpen(false)}
        onConfirm={confirmCancel}
      />

      <PartialPaymentDialog
        open={partialOpen}
        policy={policy}
        onClose={() => setPartialOpen(false)}
        onConfirm={async ({ amount, note }) => {
          setPartialOpen(false);
          const res = recordPartialPayment(policy.id, amount, note);
          if (res.status === 'invalid') { toast({ title: 'Invalid amount' }); return; }
          if (res.autoPaid) {
            toast({ title: 'Balance fully paid', description: `Sending receipt to ${policy.email}…` });
            if (!policy.receiptSentAt && policy.email) {
              try {
                const { buildEmail } = await import('../../components/agent/emailTemplateBuilder');
                const tpl = buildEmail('receipt', { ...policy, paymentStatus: 'Paid' });
                const authFetchResult = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/emails/send`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ to: policy.email, subject: tpl.subject, html: tpl.html, text: tpl.text, tag: 'receipt' }),
                }, toast);
                if (!authFetchResult) return; // 401 handled
                const { res: r, json: j } = authFetchResult;
                if (r.ok && j?.status === 'sent') {
                  updatePolicy(policy.id, { receiptSentAt: new Date().toISOString() });
                  toast({ title: 'Receipt emailed', description: `Delivered to ${policy.email}.` });
                } else {
                  toast({ title: 'Marked Paid — receipt email queued', description: 'Send it manually from the Emails panel if it does not arrive.' });
                }
              } catch (e) {
                toast({ title: 'Marked Paid — receipt email not sent', description: 'Send it manually from the Emails panel.' });
              }
            }
          } else {
            toast({ title: `£${Number(amount).toFixed(2)} recorded`, description: `£${res.outstanding.toFixed(2)} still outstanding on Plan ${policy.planNumber}.` });
          }
        }}
      />

      <SendPaperworkDialog
        open={sendOpen}
        policy={policy}
        onClose={() => setSendOpen(false)}
      />
    </div>
  );
};

export default PolicyDetail;
